// The degradation ladder — docs/01-architecture.md §5, rung table.
//
// Pure decision logic, fed one stats sample every SAMPLE_INTERVAL_MS. Each
// rung sacrifices the cheapest remaining thing per Connection > Latency >
// Quality: bitrate first (quality), then FEC/narrowband (quality), then RED +
// a bigger jitter buffer (a little latency), then TURN/TCP (real latency),
// and only rung 5 concedes to "Reconnecting…". Climbing is immediate when a
// trigger holds; descending requires RECOVERY_MS of healthy stats per rung —
// never flap.
//
// A rung's media half is applied in two places: engine.applyRungConfig() for
// what a live connection can change on the fly (sender bitrate, jitter buffer,
// relay policy), and rtc/sdp.ts for what only exists in the SDP (frame size,
// FEC, DTX, bandwidth, RED) — the latter takes effect on the next local
// description, i.e. at setup and at every ICE-restart renegotiation.

import type { LadderRung, QualityBar } from '../types.js';

export const SAMPLE_INTERVAL_MS = 2_000;

/** Stats recovered for this long ⇒ descend one rung (docs/01 §5). */
export const RECOVERY_MS = 10_000;

// Latency-first triggers (docs/01 §4.1, §5): rising RTT/jitter cuts bitrate
// long before loss appears — latency pays nothing, quality pays first.
export const RTT_DEGRADE_MS = 250;
export const JITTER_DEGRADE_MS = 30;
/** Above these, the call is visibly laggy even with zero loss → yellow bar. */
export const RTT_LAGGY_MS = 300;
export const JITTER_BUFFER_LAGGY_MS = 150;

/** Rung 2 trigger: sustained loss > 5% (two consecutive samples). */
export const LOSS_RUNG2 = 0.05;
/** Rung 3 trigger: loss > 10% despite rung 2. */
export const LOSS_RUNG3 = 0.1;
/** Rung 4 trigger: the UDP path is dying — no inbound progress this long. */
export const UDP_DYING_MS = 6_000;

/** One parsed getStats() sample (see monitor.ts for the RTCStatsReport map). */
export interface StatsSample {
  at: number;
  rttMs: number | null;
  jitterMs: number | null;
  jitterBufferDelayMs: number | null;
  /** Fraction of packets lost over the last sample window, 0..1. */
  fractionLost: number | null;
  /** Cumulative inbound audio packets received. */
  packetsReceived: number;
  /** Selected candidate-pair transport, when known. */
  transport: 'udp' | 'tcp' | 'tls' | null;
  usingRelay: boolean;
}

/** What the session must apply to the RTCPeerConnection for a rung. */
export interface RungConfig {
  rung: LadderRung;
  /** Opus target via RTCRtpSender.setParameters maxBitrate, and maxaveragebitrate. */
  maxBitrateBps: number;
  /**
   * Opus frame size (a=ptime). Packet rate is 1/ptime, and per-packet header
   * overhead (IP+UDP+RTP+ext+SRTP tag ≈ 62 B ≈ 496 bits) is paid at that rate
   * regardless of the audio bitrate:
   *
   *   20 ms → 50 pkt/s → 24.8 kbps of headers alone
   *   40 ms → 25 pkt/s → 12.4 kbps
   *   60 ms → 16.7 pkt/s → 8.3 kbps
   *
   * So on EDGE (~20-40 kbps usable uplink) a 12 kbps rung at 20 ms framing
   * still asks for ~37 kbps and oversubscribes the radio — the bitrate cut
   * buys almost nothing while headers dominate. Longer frames cost
   * packetization delay one-for-one (+20 ms at 40, +40 ms at 60), which is why
   * this grows with the rung instead of being fixed: at rung 0 the link is
   * healthy and latency wins; from rung 1 the trigger IS a filling queue, and
   * the queueing delay removed by halving the packet rate exceeds the
   * packetization delay added (Connection > Latency > Quality).
   */
  ptimeMs: number;
  /** Max FEC aggressiveness — realized as CBR + SILK-forcing limits (rtc/sdp.ts). */
  maxFec: boolean;
  /** Clamp Opus to narrowband (maxplaybackrate=8000) — rung 2's last resort. */
  narrowband: boolean;
  /** Audio RED (RFC 2198) — latency-costing, rung 3 only. */
  redEnabled: boolean;
  /** jitterBufferTarget on the receiver, ms. */
  jitterBufferTargetMs: number;
  /** Rung 4: ICE-restart onto relay-TCP/TLS only. */
  forceRelay: boolean;
}

export const RUNG_CONFIGS: readonly RungConfig[] = [
  { rung: 0, maxBitrateBps: 32_000, ptimeMs: 20, maxFec: false, narrowband: false, redEnabled: false, jitterBufferTargetMs: 20, forceRelay: false },
  { rung: 1, maxBitrateBps: 12_000, ptimeMs: 40, maxFec: false, narrowband: false, redEnabled: false, jitterBufferTargetMs: 20, forceRelay: false },
  { rung: 2, maxBitrateBps: 12_000, ptimeMs: 60, maxFec: true, narrowband: true, redEnabled: false, jitterBufferTargetMs: 40, forceRelay: false },
  { rung: 3, maxBitrateBps: 12_000, ptimeMs: 60, maxFec: true, narrowband: true, redEnabled: true, jitterBufferTargetMs: 150, forceRelay: false },
  { rung: 4, maxBitrateBps: 12_000, ptimeMs: 60, maxFec: true, narrowband: true, redEnabled: true, jitterBufferTargetMs: 150, forceRelay: true },
  // Rung 5 is not a media config: it is the Reconnecting state owned by the
  // call state machine (60 s budget). The ladder reports it; the machine acts.
  { rung: 5, maxBitrateBps: 12_000, ptimeMs: 60, maxFec: true, narrowband: true, redEnabled: true, jitterBufferTargetMs: 150, forceRelay: true },
] as const;

export interface LadderDecision {
  rung: LadderRung;
  config: RungConfig;
  bar: QualityBar;
  /** True when this sample changed the rung (session should re-apply config). */
  changed: boolean;
}

export class QualityLadder {
  private rungValue: LadderRung = 0;
  private healthySince: number | null = null;
  /** Consecutive samples with loss above each threshold (sustained triggers). */
  private lossOver5Streak = 0;
  private lossOver10Streak = 0;
  private lastPackets: number | null = null;
  private lastProgressAt: number | null = null;

  get rung(): LadderRung {
    return this.rungValue;
  }

  reset(): void {
    this.rungValue = 0;
    this.healthySince = null;
    this.lossOver5Streak = 0;
    this.lossOver10Streak = 0;
    this.lastPackets = null;
    this.lastProgressAt = null;
  }

  evaluate(s: StatsSample): LadderDecision {
    const prev = this.rungValue;

    // --- inbound progress tracking (rung 4/5 trigger) ----------------------
    if (this.lastPackets === null || s.packetsReceived > this.lastPackets) {
      this.lastProgressAt = s.at;
    }
    this.lastPackets = s.packetsReceived;
    const stalledMs = this.lastProgressAt === null ? 0 : s.at - this.lastProgressAt;

    // --- sustained-loss streaks --------------------------------------------
    if (s.fractionLost !== null && s.fractionLost > LOSS_RUNG2) this.lossOver5Streak++;
    else this.lossOver5Streak = 0;
    if (s.fractionLost !== null && s.fractionLost > LOSS_RUNG3) this.lossOver10Streak++;
    else this.lossOver10Streak = 0;

    // --- the trigger cascade: worst active trigger wins ---------------------
    const latencyRising =
      (s.rttMs !== null && s.rttMs > RTT_DEGRADE_MS) ||
      (s.jitterMs !== null && s.jitterMs > JITTER_DEGRADE_MS);
    const sustainedLoss5 = this.lossOver5Streak >= 2;
    const sustainedLoss10 = this.lossOver10Streak >= 2;
    const udpDying = stalledMs >= UDP_DYING_MS && !s.usingRelay;
    const noPath = stalledMs >= UDP_DYING_MS && s.usingRelay;

    let target: LadderRung = 0;
    if (latencyRising) target = 1;
    if (sustainedLoss5) target = 2;
    // Rung 3 only "despite rung 2": we must already be paying rung 2's price.
    if (sustainedLoss10 && this.rungValue >= 2) target = 3;
    if (udpDying) target = 4;
    if (noPath) target = 5;

    // --- climb immediately, descend only after RECOVERY_MS healthy ----------
    if (target > this.rungValue) {
      this.rungValue = target;
      this.healthySince = null;
    } else if (target < this.rungValue) {
      if (this.healthySince === null) this.healthySince = s.at;
      if (s.at - this.healthySince >= RECOVERY_MS) {
        this.rungValue = (this.rungValue - 1) as LadderRung;
        // each further descent needs its own healthy window
        this.healthySince = s.at;
      }
    } else {
      this.healthySince = null;
    }

    const rung = this.rungValue;
    return {
      rung,
      config: RUNG_CONFIGS[rung]!,
      bar: barFor(rung, s),
      changed: rung !== prev,
    };
  }
}

/**
 * Connection bar (docs/06 §3): latency is a first-class signal — a laggy call
 * shows yellow even at 0% loss.
 */
export function barFor(rung: LadderRung, s: StatsSample): QualityBar {
  if (rung >= 3) return 'red';
  const laggy =
    (s.rttMs !== null && s.rttMs > RTT_LAGGY_MS) ||
    (s.jitterBufferDelayMs !== null && s.jitterBufferDelayMs > JITTER_BUFFER_LAGGY_MS);
  if (rung >= 1 || laggy) return 'yellow';
  return 'green';
}
